import { fetchJSON as fetchAPI } from './client';

export type MarketDashboard = {
  tenant_id: string;
  categories_tracked: number;
  competitors_observed: number;
  active_opportunities: number;
  emerging_trends: number;
  avg_category_growth_pct: number;
  avg_competitive_pressure: number;
  top_opportunity_score: number;
  active_recommendations: number;
  market_readiness_avg: number;
};

export type MarketSignal = {
  id: string;
  category: string;
  signal_type: string;
  title: string;
  description?: string;
  strength: number;
  source?: string;
};

export type CategoryTrend = {
  id: string;
  category: string;
  growth_pct: number;
  competitive_density: number;
  content_quality_avg: number;
  price_median: number;
  direction: string;
  emerging_niche: boolean;
};

export type CompetitiveObservation = {
  id: string;
  competitor: string;
  asin?: string;
  category: string;
  listing_quality: number;
  content_completeness: number;
  visual_quality: number;
  pricing_position?: string;
  review_count: number;
  rating_avg: number;
  promotion_active: boolean;
};

export type OpportunityScore = {
  id: string;
  title: string;
  type: string;
  category?: string;
  sku?: string;
  category_opportunity_score: number;
  product_opportunity_score: number;
  competitive_pressure_score: number;
  market_readiness_score: number;
  innovation_score: number;
  growth_potential_score: number;
  composite_score: number;
  evidence?: string;
};

export type TrendRecord = {
  id: string;
  name: string;
  type: string;
  category?: string;
  direction: string;
  strength: number;
  description?: string;
};

export type MarketRecommendation = {
  id: string;
  type: string;
  title: string;
  description?: string;
  evidence?: string;
  category?: string;
  sku?: string;
  expected_impact?: string;
  confidence_score: number;
  priority_rank: number;
  requires_approval: boolean;
};

export type PortfolioStrategy = {
  id: string;
  action: string;
  title: string;
  rationale?: string;
  category?: string;
  sku?: string;
  priority_score: number;
};

export type ScenarioRecord = {
  id: string;
  name: string;
  type: string;
  outcome?: string;
  revenue_impact?: number;
  profit_impact?: number;
  inventory_impact?: number;
  created_at: string;
};

const API_BASE = import.meta.env.VITE_MARKETINTEL_API ?? '/api/v1/marketintel';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const marketintelApi = {
  dashboard: () => fetchJSON<MarketDashboard>('/dashboard'),
  listSignals: () => fetchJSON<MarketSignal[]>('/market'),
  listCategories: () => fetchJSON<CategoryTrend[]>('/categories'),
  listCompetitive: () => fetchJSON<CompetitiveObservation[]>('/competitive'),
  listOpportunities: () => fetchJSON<OpportunityScore[]>('/opportunities'),
  listTrends: () => fetchJSON<TrendRecord[]>('/trends'),
  listPortfolio: () => fetchJSON<PortfolioStrategy[]>('/portfolio'),
  listRecommendations: () => fetchJSON<MarketRecommendation[]>('/recommendations'),
  generateRecommendations: () => fetchJSON<MarketRecommendation[]>('/recommendations/generate', { method: 'POST' }),
  approveRecommendation: (id: string) =>
    fetchJSON<MarketRecommendation>(`/recommendations/${id}/approve`, { method: 'POST', body: JSON.stringify({}) }),
  listScenarios: () => fetchJSON<ScenarioRecord[]>('/scenarios'),
  runScenario: (body: { name: string; type: string }) =>
    fetchJSON<ScenarioRecord>('/scenarios', { method: 'POST', body: JSON.stringify(body) }),
  listActivity: () => fetchJSON<{ id: string; message: string }[]>('/activity'),
};
